import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { far } from "@fortawesome/pro-regular-svg-icons";
import PropTypes from "prop-types";
import { injectIntl, defineMessages } from "react-intl";
import { Card, Col, Row, Badge } from "react-bootstrap";
import StatusBadge from "./StatusBadge";
import PaydateText from "./PaydateText";
import useEnv from "../hooks/useEnv";
import useFormatter from "../hooks/useFormatter";
import useJob from "../hooks/useJob";
import isEmpty from "lodash.isempty";

// Strings for translation in this view
const messages = defineMessages({
    creditor: {
        id: 'Laskuttaja',
        defaultMessage: 'Laskuttaja'
    },
    dueDate: {
        id: 'Eräpäivä',
        defaultMessage: 'Eräpäivä'
    },
    openTotal: {
        id: 'Avoinna',
        defaultMessage: 'Avoinna'
    },
    paidTotal: {
        id: 'Maksettu',
        defaultMessage: 'Maksettu'
    },
    status: {
        id: 'Tila',
        defaultMessage: 'Tila'
    },
    signServing: {
        id: "Allekirjoita tiedoksianto",
        defaultMessage: "Allekirjoita tiedoksianto"
    },
    openInvoiceLabel: {
        id: "Avaa laskusivu",
        defaultMessage: "Avaa laskusivu"
    },
    amount: {
        id: 'Määrä',
        defaultMessage: 'Määrä'
    },
    paymentAttemptFailed: {
        id: "Maksuyritys epäonnistui {date}",
        defaultMessage: "Maksuyritys epäonnistui {date}"
    }
});
export { messages };

const InvoiceCard = (props) => {

    // Translations function
    const lng = props.intl.formatMessage;
    const { getEnv, isEnvTruthy } = useEnv();
    const { formatCurrency, formatDate } = useFormatter();
    const { lastPaymentFailedAt } = useJob(props.job);

    const creditor = props.job.payee?.name;
    const openTotal = props.job.openamount;
    const paidTotal = props.job.paidamount;
    const statusCode = props.job.job_main_statuscode;
    const jobStatus = props.job.jobstatus;
    const dueDate = props.job.paydate;
    const signatureNeeded = (isEnvTruthy('SHOW_SERVING') && props.job.serving_data?.data?.serving_type === 'letter' && props.job.serving_data?.data?.serving_need_signature);
    const currency = isEmpty(props.job.currency) ? getEnv('DEFAULT_CURRENCY') : props.job.currency;

    const resolveMoneyTitle = () => {
        if (props.job.job_main_statuscode === 122) {
            return lng(messages.amount);
        }
        const parts = [];
        if (openTotal > 0) {
            parts.push(lng(messages.openTotal));
        }
        if (openTotal > 0 && paidTotal > 0) {
            parts.push('/');
        }
        if (paidTotal > 0) {
            parts.push(lng(messages.paidTotal));
        }
        return parts.join(' ');
    }

    const resolveMoneyValue = () => {
        if (props.job.job_main_statuscode === 122) {
            return formatCurrency(paidTotal, currency);
        }
        const parts = [];
        if (openTotal > 0) {
            parts.push(formatCurrency(openTotal, currency));
        }
        if (openTotal > 0 && paidTotal > 0) {
            parts.push('/');
        }
        if (paidTotal > 0) {
            parts.push(formatCurrency(paidTotal, currency));
        }
        return parts.join(' ');
    }

    return (
        <Card className={"invoice invoice-card" + (signatureNeeded ? " warning" : "")} aria-label={lng(messages.openInvoiceLabel)}>
            <Card.Body>
                <Row>
                    <Col xs={6} lg={3} className="mb-2 mb-md-0">
                        <p className="text-muted fs-6 mb-0">{lng(messages.creditor)}</p>
                        <h5 className="text-break">{creditor}</h5>
                    </Col>
                    <Col xs={6} lg={3}>
                        <p className="text-muted fs-6 mb-0">{lng(messages.dueDate)}</p>
                        <h5>
                            <PaydateText
                                intl={props.intl}
                                statuscode={jobStatus ?? null}
                                paydate={dueDate ?? null}
                            />
                        </h5>
                    </Col>
                    <Col xs={6} lg={3}>
                        <p className="text-muted fs-6 mb-0">{resolveMoneyTitle()}</p>
                        <h5>{resolveMoneyValue()}</h5>
                    </Col>
                    <Col xs={6} lg={3}>
                        <p className="text-muted fs-6 mb-0">{lng(messages.status)}</p>
                        {signatureNeeded ? (
                            <Badge bg="warning" className="text-wrap">{lng(messages.signServing)}</Badge>
                        ) : (
                            <StatusBadge statusCode={statusCode} dueDate={dueDate} lng={lng} />
                        )}
                    </Col>
                </Row>
                {lastPaymentFailedAt !== null && (
                    <Row className='pt-2'>
                        <hr />
                        <div className='pt-2'>
                            <FontAwesomeIcon className='text-warning' icon={far.faWarning} aria-hidden="true" />
                            <span className='ps-2 text-light'>
                                {lng(messages.paymentAttemptFailed, { date: formatDate(lastPaymentFailedAt) })}
                            </span>
                        </div>
                    </Row>
                )}
            </Card.Body>
            <div className={"invoice-card-icon" + (signatureNeeded ? " warning" : "")}>
                <FontAwesomeIcon icon={far.faChevronRight} aria-hidden="true" />
            </div>
        </Card>
    );
};

InvoiceCard.propTypes = {
    'intl': PropTypes.object.isRequired,
    'job': PropTypes.object.isRequired
};

export default injectIntl(InvoiceCard);
